import { useState } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/AppShell';
import { useAuth } from '../lib/auth';

export function AuthPage() {
  const navigate = useNavigate();
  const { error, signInWithEmail, signInWithGoogle, registerWithEmail, clearError } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submitEmail(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'register') {
        await registerWithEmail(displayName, email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate('/feed');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitGoogle() {
    setSubmitting(true);
    try {
      await signInWithGoogle();
      navigate('/feed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="auth-page page-pad">
      <Link className="icon-link" to="/feed" aria-label="Back to feed">
        <ArrowLeft size={21} />
      </Link>
      <div className="auth-brand">
        <BrandMark />
      </div>
      <h1>{mode === 'register' ? 'Create your account' : 'Sign in'}</h1>
      <p className="page-lede">Resume watching, vote on what ships next, and submit your show ideas.</p>

      {error && (
        <button className="error-banner" type="button" onClick={clearError}>
          {error}
        </button>
      )}

      <button className="google-button" type="button" onClick={submitGoogle} disabled={submitting}>
        <Mail size={18} aria-hidden="true" />
        {submitting ? 'Connecting...' : 'Continue with Google'}
      </button>

      <div className="divider">or</div>

      <form className="stack-form" onSubmit={submitEmail}>
        {mode === 'register' && (
          <label>
            <span>Display name</span>
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} required minLength={2} />
          </label>
        )}
        <label>
          <span>Email</span>
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required />
        </label>
        <label>
          <span>Password</span>
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            required
            minLength={6}
          />
        </label>
        <button className="primary-action" type="submit" disabled={submitting}>
          {submitting ? 'Working...' : mode === 'register' ? 'Create account' : 'Sign in'}
        </button>
      </form>

      <button className="text-switch" type="button" onClick={() => setMode(mode === 'register' ? 'login' : 'register')}>
        {mode === 'register' ? 'Already have an account? Sign in' : 'No account yet? Create one'}
      </button>
    </section>
  );
}
